/**
 * Global Mixin for Vue Components
 *
 * This mixin provides:
 *
 * 1. Access to cached master data from Vuex store
 *    - Eliminates redundant API calls for master data
 *    - Provides consistent data access across components
 *
 * 2. selectMultipleAll(variableKey, datasetKey)
 *    - Toggles selection of all items in a dataset.
 *    - Params:
 *      - variableKey (String): Key name of the selected items array in the component's data.
 *      - datasetKey (String): Key name of the full dataset array in the component's data.
 *    - Behavior:
 *      - If all items are already selected, it clears the selection.
 *      - Otherwise, selects all item IDs.
 *
 * 3. _globalFocusShortcut(event)
 *    - Focuses on the input field with ref="searchInput" when Alt + B is pressed.
 *    - Should be used in combination with ref="searchInput" in component templates.
 *
 * Lifecycle Hooks:
 * - mounted:
 *    - Adds a keydown event listener for Alt + B.
 * - beforeDestroy:
 *    - Removes the keydown event listener.
 *
 * Vuex Master Data Exposed (Cached):
 * - USERS_DATASET
 * - ACTIVE_USERS_DATASET
 * - DEPARTMENTS_DATASET
 * - ACTIVE_DEPARTMENTS_DATASET
 * - SUB_DEPARTMENTS_DATASET
 * - ACTIVE_SUB_DEPARTMENTS_DATASET
 * - DESIGNATIONS_DATASET
 * - ACTIVE_DESIGNATIONS_DATASET
 * - FUNCTIONS_DATASET
 * - ACTIVE_FUNCTIONS_DATASET
 * - ROLES_DATASET
 * - ACTIVE_ROLES_DATASET
 * - CLIENTS_DATASET
 * - ACTIVE_CLIENTS_DATASET
 * - INDUSTRIES_DATASET
 * - ACTIVE_INDUSTRIES_DATASET
 * - TEMPLATE_CATEGORIES_DATASET
 * - ACTIVE_TEMPLATE_CATEGORIES_DATASET
 * - KPI_ROLES_DATASET
 * - ACTIVE_KPI_ROLES_DATASET
 * - KPIS_DATASET
 * - ACTIVE_KPIS_DATASET
 * - SERVICE_CATEGORIES_DATASET
 * - ACTIVE_SERVICE_CATEGORIES_DATASET
 * - SERVICES_DATASET
 * - ACTIVE_SERVICES_DATASET
 * - CHECKLISTS_DATASET
 * - ACTIVE_CHECKLISTS_DATASET
 * - CHECKLIST_TASKS_DATASET
 * - ACTIVE_CHECKLIST_TASKS_DATASET
 * - CHECKLIST_SUBTASKS_DATASET
 * - ACTIVE_CHECKLIST_SUBTASKS_DATASET
 * - EMPLOYEES_DATASET — REMOVED (fetched per-screen via /employees API)
 * - ACTIVE_EMPLOYEES_DATASET — REMOVED (fetched per-screen via /employees API)
 * - APPRAISAL_DETAILS_DATASET
 * - ACTIVE_APPRAISAL_DETAILS_DATASET
 * - APPRAISAL_PERIODS_DATASET
 * - ACTIVE_APPRAISAL_PERIODS_DATASET
 * - LANDING_DATA_DATASET
 * - COMMON_SETTINGS_DATASET
 * - SCREEN_RIGHTS_DATASET
 * - MODULE_RIGHTS_DATASET
 * - UOMS_DATASET
 * - ACTIVE_UOMS_DATASET
 * - KPI_TYPES_DATASET
 * - ACTIVE_KPI_TYPES_DATASET
 * - TARGET_TYPES_DATASET
 * - ACTIVE_TARGET_TYPES_DATASET
 * - CHANNELS_DATASET
 * - ACTIVE_CHANNELS_DATASET
 * - SUB_CHANNELS_DATASET
 * - ACTIVE_SUB_CHANNELS_DATASET
 * - DIVISIONS_DATASET
 * - ACTIVE_DIVISIONS_DATASET
 * - CATEGORIES_DATASET
 * - ACTIVE_CATEGORIES_DATASET
 * - STORES_DATASET
 * - ACTIVE_STORES_DATASET
 * - PERFORMANCE_RATINGS_DATASET
 * - ACTIVE_PERFORMANCE_RATINGS_DATASET
 * - TICKETS_DATASET
 * - CLIENT_TYPES_DATASET
 * - ACTIVE_CLIENT_TYPES_DATASET
 * - POLICY_TYPES_DATASET
 * - ACTIVE_POLICY_TYPES_DATASET
 * - ROUTES_DATASET
 * - ACTIVE_ROUTES_DATASET
 * - PAYMENT_RECEIVED_DATASET
 * - ACTIVE_PAYMENT_RECEIVED_DATASET
 * - TICKET_CATEGORIES_DATASET
 * - ACTIVE_TICKET_CATEGORIES_DATASET
 * - LOCATIONS_DATASET
 * - ACTIVE_LOCATIONS_DATASET
 * - CUSTOMER_GROUPS_DATASET
 * - ACTIVE_CUSTOMER_GROUPS_DATASET
 * - CUSTOMER_GRADES_DATASET
 * - ACTIVE_CUSTOMER_GRADES_DATASET
 */
import { mapGetters } from 'vuex';

export default {
  computed: {
    ...mapGetters({
      // Users
      // USERS_DATASET: 'masterData/getUsers',
      ACTIVE_USERS_DATASET: 'masterData/getActiveUsers',

      // Departments
      // DEPARTMENTS_DATASET: 'masterData/getDepartments',
      ACTIVE_DEPARTMENTS_DATASET: 'masterData/getActiveDepartments',

      // Sub-Departments
      // SUB_DEPARTMENTS_DATASET: 'masterData/getSubDepartments',
      ACTIVE_SUB_DEPARTMENTS_DATASET: 'masterData/getActiveSubDepartments',

      // Designations
      // DESIGNATIONS_DATASET: 'masterData/getDesignations',
      ACTIVE_DESIGNATIONS_DATASET: 'masterData/getActiveDesignations',

      // Functions
      // FUNCTIONS_DATASET: 'masterData/getFunctions',
      ACTIVE_FUNCTIONS_DATASET: 'masterData/getActiveFunctions',

      // Roles
      // ROLES_DATASET: 'masterData/getRoles',
      ACTIVE_ROLES_DATASET: 'masterData/getActiveRoles',

      // Clients
      // CLIENTS_DATASET: 'masterData/getClients',
      ACTIVE_CLIENTS_DATASET: 'masterData/getActiveClients',

      // Template Categories
      // TEMPLATE_CATEGORIES_DATASET: 'masterData/getTemplateCategories',
      ACTIVE_TEMPLATE_CATEGORIES_DATASET: 'masterData/getActiveTemplateCategories',

      // KPI Roles
      // KPI_ROLES_DATASET: 'masterData/getKpiRoles',
      ACTIVE_KPI_ROLES_DATASET: 'masterData/getActiveKpiRoles',

      // KPIs
      // KPIS_DATASET: 'masterData/getKpis',
      ACTIVE_KPIS_DATASET: 'masterData/getActiveKpis',

      // Service Categories
      // SERVICE_CATEGORIES_DATASET: 'masterData/getServiceCategories',
      ACTIVE_SERVICE_CATEGORIES_DATASET: 'masterData/getActiveServiceCategories',

      // Services
      // SERVICES_DATASET: 'masterData/getServices',
      ACTIVE_SERVICES_DATASET: 'masterData/getActiveServices',

      // Checklists
      // CHECKLISTS_DATASET: 'masterData/getChecklists',
      ACTIVE_CHECKLISTS_DATASET: 'masterData/getActiveChecklists',

      // Checklist Tasks
      // CHECKLIST_TASKS_DATASET: 'masterData/getChecklistTasks',
      ACTIVE_CHECKLIST_TASKS_DATASET: 'masterData/getActiveChecklistTasks',

      // Checklist Subtasks
      // CHECKLIST_SUBTASKS_DATASET: 'masterData/getChecklistSubtasks',
      ACTIVE_CHECKLIST_SUBTASKS_DATASET: 'masterData/getActiveChecklistSubtasks',

      // Employees — REMOVED (10k+ record bloat).
      // Screens that need employee data call GET /employees?entity_id=X&active=true
      // directly. See promotions.vue for the reference pattern.

      // Appraisal Details
      APPRAISAL_DETAILS_DATASET: 'masterData/getAppraisalDetails',
      ACTIVE_APPRAISAL_DETAILS_DATASET: 'masterData/getActiveAppraisalDetails',

      // Appraisal Periods
      APPRAISAL_PERIODS_DATASET: 'masterData/getAppraisalPeriods',
      ACTIVE_APPRAISAL_PERIODS_DATASET: 'masterData/getActiveAppraisalPeriods',

      // Landing Data (Health Check)
      LANDING_DATA_DATASET: 'masterData/getLandingData',

      // Common Settings
      COMMON_SETTINGS_DATASET: 'masterData/getCommonSettings',

      // Screen Rights
      SCREEN_RIGHTS_DATASET: 'masterData/getScreenRights',

      // Module Rights
      MODULE_RIGHTS_DATASET: 'masterData/getModuleRights',

      // UOMs (Units of Measurement)
      // UOMS_DATASET: 'masterData/getUoms',
      ACTIVE_UOMS_DATASET: 'masterData/getActiveUoms',

      // KPI Types
      // KPI_TYPES_DATASET: 'masterData/getKpiTypes',
      ACTIVE_KPI_TYPES_DATASET: 'masterData/getActiveKpiTypes',

      // Target Types
      // TARGET_TYPES_DATASET: 'masterData/getTargetTypes',
      ACTIVE_TARGET_TYPES_DATASET: 'masterData/getActiveTargetTypes',

      // Channels
      // CHANNELS_DATASET: 'masterData/getChannels',
      ACTIVE_CHANNELS_DATASET: 'masterData/getActiveChannels',

      // Sub-Channels
      // SUB_CHANNELS_DATASET: 'masterData/getSubChannels',
      ACTIVE_SUB_CHANNELS_DATASET: 'masterData/getActiveSubChannels',

      // Divisions
      // DIVISIONS_DATASET: 'masterData/getDivisions',
      ACTIVE_DIVISIONS_DATASET: 'masterData/getActiveDivisions',

      // Categories
      // CATEGORIES_DATASET: 'masterData/getCategories',
      ACTIVE_CATEGORIES_DATASET: 'masterData/getActiveCategories',

      // Stores
      // STORES_DATASET: 'masterData/getStores',
      ACTIVE_STORES_DATASET: 'masterData/getActiveStores',

      // Performance Ratings
      // PERFORMANCE_RATINGS_DATASET: 'masterData/getPerformanceRatings',
      ACTIVE_PERFORMANCE_RATINGS_DATASET: 'masterData/getActivePerformanceRatings',

      // Holidays
      HOLIDAYS_DATASET: 'masterData/getHolidays',

      // Weekoffs
      WEEKOFFS_DATASET: 'masterData/getWeekoffs',

      // Branches
      // BRANCHES_DATASET: 'masterData/getBranches',
      ACTIVE_BRANCHES_DATASET: 'masterData/getActiveBranches',

      // Tickets
      TICKETS_DATASET: 'masterData/getTickets',

      // Form Maintenance
      // FORM_MAINTENANCES_DATASET: 'masterData/getFormMaintenances',
      ACTIVE_FORM_MAINTENANCES_DATASET: 'masterData/getActiveFormMaintenances',

      // Forms Master
      // FORMS_MASTERS_DATASET: 'masterData/getFormsMasters',
      ACTIVE_FORMS_MASTERS_DATASET: 'masterData/getActiveFormsMasters',

      // Slabs
      // SLABS_DATASET: 'masterData/getSlabs',
      ACTIVE_SLABS_DATASET: 'masterData/getActiveSlabs',

      // Processes
      // PROCESSES_DATASET: 'masterData/getProcesses',
      ACTIVE_PROCESSES_DATASET: 'masterData/getActiveProcesses',

      // Industries
      // INDUSTRIES_DATASET: 'masterData/getIndustries',
      ACTIVE_INDUSTRIES_DATASET: 'masterData/getActiveIndustries',

      // Client Types
      // CLIENT_TYPES_DATASET: 'masterData/getClientTypes',
      ACTIVE_CLIENT_TYPES_DATASET: 'masterData/getActiveClientTypes',

      // Policy Types
      // POLICY_TYPES_DATASET: 'masterData/getPolicyTypes',
      ACTIVE_POLICY_TYPES_DATASET: 'masterData/getActivePolicyTypes',

      // Routes
      // ROUTES_DATASET: 'masterData/getRoutes',
      ACTIVE_ROUTES_DATASET: 'masterData/getActiveRoutes',

      // Payment Received
      // PAYMENT_RECEIVED_DATASET: 'masterData/getPaymentReceived',
      ACTIVE_PAYMENT_RECEIVED_DATASET: 'masterData/getActivePaymentReceived',

      // Ticket Categories
      // TICKET_CATEGORIES_DATASET: 'masterData/getTicketCategories',
      ACTIVE_TICKET_CATEGORIES_DATASET: 'masterData/getActiveTicketCategories',

      // Locations
      // LOCATIONS_DATASET: 'masterData/getLocations',
      ACTIVE_LOCATIONS_DATASET: 'masterData/getActiveLocations',

      // Customer Groups
      // CUSTOMER_GROUPS_DATASET: 'masterData/getCustomerGroups',
      ACTIVE_CUSTOMER_GROUPS_DATASET: 'masterData/getActiveCustomerGroups',

      // Customer Grades
      // CUSTOMER_GRADES_DATASET: 'masterData/getCustomerGrades',
      ACTIVE_CUSTOMER_GRADES_DATASET: 'masterData/getActiveCustomerGrades',
    }),
  },
  mounted() {
    window.addEventListener("keydown", this._globalFocusShortcut);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this._globalFocusShortcut);
  },
  methods: {
    /**
     * Toggle select all functionality for multi-select components
     * @param {String} variableKey - The key of the selected items array in component data
     * @param {String} datasetKey - The key of the full dataset array in component data
     */
    selectMultipleAll(variableKey, datasetKey) {
      this.$nextTick(() => {
        if ((this[variableKey] || []).length === (this[datasetKey] || []).length) {
          this[variableKey] = [];
        } else {
          this[variableKey] = this[datasetKey].map((a) => a.id);
        }
      });
    },

    /**
     * Global keyboard shortcut for search input focus
     * Press Alt + B to focus on search input
     * @param {KeyboardEvent} e - The keyboard event
     */
    _globalFocusShortcut(e) {
      if (e.altKey && e.key.toLowerCase() === "b") {
        e.preventDefault();
        this.$nextTick(() => {
          this.$refs?.searchInput?.focus?.();
        });
      }
    },

    /**
     * Refresh master data cache
     * Call this after CRUD operations on master data to ensure fresh data
     */
    async refreshMasterDataCache() {
      try {
        await this.$store.dispatch('masterData/refreshMasterData');
        this.global_push_toast('success', 'Master data refreshed successfully');
      } catch (error) {
        console.error('Error refreshing master data:', error);
        this.global_push_toast('error', 'Failed to refresh master data');
      }
    },

    /**
     * Fetch services and service categories data on-demand
     * Call this in ticket pages that need service/service category data
     */
    async fetchServicesDataIfNeeded() {
      try {
        await this.$store.dispatch('masterData/fetchServicesData');
      } catch (error) {
        console.error('Error fetching services data:', error);
      }
    },

    /**
     * Fetch tickets data on-demand
     * Call this in ticket report pages that need tickets data
     */
    async fetchTicketsDataIfNeeded() {
      try {
        console.log('[fetchTicketsDataIfNeeded] Starting fetch...');
        await this.$store.dispatch('masterData/fetchTicketsData');
        console.log('[fetchTicketsDataIfNeeded] Fetch completed, TICKETS_DATASET:', this.TICKETS_DATASET);
      } catch (error) {
        console.error('Error fetching tickets data:', error);
      }
    },

    /**
     * Fetch form maintenance data on-demand
     * Call this in checklist pages that need form maintenance data
     */
    async fetchFormMaintenancesDataIfNeeded() {
      try {
        console.log('[fetchFormMaintenancesDataIfNeeded] Starting fetch...');
        await this.$store.dispatch('masterData/fetchFormMaintenancesData');
        console.log('[fetchFormMaintenancesDataIfNeeded] Fetch completed, FORM_MAINTENANCES_DATASET:', this.FORM_MAINTENANCES_DATASET);
      } catch (error) {
        console.error('Error fetching form maintenance data:', error);
      }
    },

    /**
     * Fetch forms master data on-demand
     * Call this in checklist pages that need forms master data (filing forms dropdown)
     */
    async fetchFormsMastersDataIfNeeded() {
      try {
        console.log('[fetchFormsMastersDataIfNeeded] Starting fetch...');
        await this.$store.dispatch('masterData/fetchFormsMastersData');
        console.log('[fetchFormsMastersDataIfNeeded] Fetch completed, FORMS_MASTERS_DATASET:', this.FORMS_MASTERS_DATASET);
      } catch (error) {
        console.error('Error fetching forms master data:', error);
      }
    },

    /**
     * Fetch processes data on-demand
     * Call this in checklist template pages that need process data
     */
    async fetchProcessesDataIfNeeded() {
      try {
        console.log('[fetchProcessesDataIfNeeded] Starting fetch...');
        await this.$store.dispatch('masterData/fetchProcessesData');
        console.log('[fetchProcessesDataIfNeeded] Fetch completed, PROCESSES_DATASET:', this.PROCESSES_DATASET);
      } catch (error) {
        console.error('Error fetching processes data:', error);
      }
    },

    /**
     * Refresh only client types data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches client types
     */
    async refreshClientTypesCache() {
      try {
        await this.$store.dispatch('masterData/refreshClientTypes');
      } catch (error) {
        console.error('Error refreshing client types:', error);
      }
    },

    /**
     * Refresh only locations data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches locations
     */
    async refreshLocationsCache() {
      try {
        await this.$store.dispatch('masterData/refreshLocations');
      } catch (error) {
        console.error('Error refreshing locations:', error);
      }
    },

    /**
     * Refresh only policy types data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches policy types
     */
    async refreshPolicyTypesCache() {
      try {
        await this.$store.dispatch('masterData/refreshPolicyTypes');
      } catch (error) {
        console.error('Error refreshing policy types:', error);
      }
    },

    /**
     * Refresh only routes data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches routes
     */
    async refreshRoutesCache() {
      try {
        await this.$store.dispatch('masterData/refreshRoutes');
      } catch (error) {
        console.error('Error refreshing routes:', error);
      }
    },

    /**
     * Refresh only ticket categories data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches ticket categories
     */
    async refreshTicketCategoriesCache() {
      try {
        await this.$store.dispatch('masterData/refreshTicketCategories');
      } catch (error) {
        console.error('Error refreshing ticket categories:', error);
      }
    },

    /**
     * Refresh only payment received data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches payment received
     */
    async refreshPaymentReceivedCache() {
      try {
        await this.$store.dispatch('masterData/refreshPaymentReceived');
      } catch (error) {
        console.error('Error refreshing payment received:', error);
      }
    },

    /**
     * Refresh only customer groups data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches customer groups
     */
    async refreshCustomerGroupsCache() {
      try {
        await this.$store.dispatch('masterData/refreshCustomerGroups');
      } catch (error) {
        console.error('Error refreshing customer groups:', error);
      }
    },

    /**
     * Refresh only service categories data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches service categories
     */
    async refreshServiceCategoriesCache() {
      try {
        await this.$store.dispatch('masterData/refreshServiceCategories');
      } catch (error) {
        console.error('Error refreshing service categories:', error);
      }
    },

    /**
     * Refresh only services data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches services
     */
    async refreshServicesCache() {
      try {
        await this.$store.dispatch('masterData/refreshServices');
      } catch (error) {
        console.error('Error refreshing services:', error);
      }
    },

    /**
     * Refresh only departments data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches departments
     */
    async refreshDepartmentsCache() {
      try {
        await this.$store.dispatch('masterData/refreshDepartments');
      } catch (error) {
        console.error('Error refreshing departments:', error);
      }
    },

    /**
     * Refresh only subdepartments data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches subdepartments
     */
    async refreshSubDepartmentsCache() {
      try {
        await this.$store.dispatch('masterData/refreshSubDepartments');
      } catch (error) {
        console.error('Error refreshing subdepartments:', error);
      }
    },

    /**
     * Refresh only designations data after CRUD operations
     * More efficient than refreshMasterDataCache() as it only fetches designations
     */
    async refreshDesignationsCache() {
      try {
        await this.$store.dispatch('masterData/refreshDesignations');
      } catch (error) {
        console.error('Error refreshing designations:', error);
      }
    },

    async refreshUsersCache() {
      try {
        await this.$store.dispatch('masterData/refreshUsers');
      } catch (error) {
        console.error('Error refreshing users:', error);
      }
    },

    // refreshEmployeesCache() removed — employees no longer cached in Vuex.
    // Components re-fetch via /employees API when they need fresh data.

    async refreshClientsCache() {
      try {
        await this.$store.dispatch('masterData/refreshClients');
      } catch (error) {
        console.error('Error refreshing clients:', error);
      }
    },

    async refreshKpisCache() {
      try {
        await this.$store.dispatch('masterData/refreshKpis');
      } catch (error) {
        console.error('Error refreshing KPIs:', error);
      }
    },

    async refreshKpiTypesCache() {
      try {
        await this.$store.dispatch('masterData/refreshKpiTypes');
      } catch (error) {
        console.error('Error refreshing KPI types:', error);
      }
    },

    async refreshTargetTypesCache() {
      try {
        await this.$store.dispatch('masterData/refreshTargetTypes');
      } catch (error) {
        console.error('Error refreshing target types:', error);
      }
    },

    async refreshUomsCache() {
      try {
        await this.$store.dispatch('masterData/refreshUoms');
      } catch (error) {
        console.error('Error refreshing UOMs:', error);
      }
    },

    async refreshPerformanceRatingsCache() {
      try {
        await this.$store.dispatch('masterData/refreshPerformanceRatings');
      } catch (error) {
        console.error('Error refreshing performance ratings:', error);
      }
    },

    async refreshAppraisalDetailsCache() {
      try {
        await this.$store.dispatch('masterData/refreshAppraisalDetails');
      } catch (error) {
        console.error('Error refreshing appraisal details:', error);
      }
    },

    async refreshAppraisalPeriodsCache() {
      try {
        await this.$store.dispatch('masterData/refreshAppraisalPeriods');
      } catch (error) {
        console.error('Error refreshing appraisal periods:', error);
      }
    },

    async refreshChecklistsCache() {
      try {
        await this.$store.dispatch('masterData/refreshChecklists');
      } catch (error) {
        console.error('Error refreshing checklists:', error);
      }
    },

    async refreshChecklistTasksCache() {
      try {
        await this.$store.dispatch('masterData/refreshChecklistTasks');
      } catch (error) {
        console.error('Error refreshing checklist tasks:', error);
      }
    },

    async refreshChecklistSubtasksCache() {
      try {
        await this.$store.dispatch('masterData/refreshChecklistSubtasks');
      } catch (error) {
        console.error('Error refreshing checklist subtasks:', error);
      }
    },

    async refreshEmployeeSettingsCache() {
      try {
        await this.$store.dispatch('masterData/refreshEmployeeSettings');
      } catch (error) {
        console.error('Error refreshing employee settings:', error);
      }
    },

    async refreshHolidaysCache() {
      try {
        await this.$store.dispatch('masterData/refreshHolidays');
      } catch (error) {
        console.error('Error refreshing holidays:', error);
      }
    },

    async refreshWeekoffsCache() {
      try {
        await this.$store.dispatch('masterData/refreshWeekoffs');
      } catch (error) {
        console.error('Error refreshing weekoffs:', error);
      }
    },

    async refreshTenantMastersCache() {
      try {
        await this.$store.dispatch('masterData/refreshTenantMasters');
      } catch (error) {
        console.error('Error refreshing tenant masters:', error);
      }
    },

    async refreshBranchesCache() {
      try {
        await this.$store.dispatch('masterData/refreshBranches');
      } catch (error) {
        console.error('Error refreshing branches:', error);
      }
    },

    async refreshTicketsCache() {
      try {
        await this.$store.dispatch('masterData/refreshTickets');
      } catch (error) {
        console.error('Error refreshing tickets:', error);
      }
    },

    async refreshFormMaintenancesCache() {
      try {
        await this.$store.dispatch('masterData/refreshFormMaintenances');
      } catch (error) {
        console.error('Error refreshing form maintenances:', error);
      }
    },

    async refreshFormsMastersCache() {
      try {
        await this.$store.dispatch('masterData/refreshFormsMasters');
      } catch (error) {
        console.error('Error refreshing forms masters:', error);
      }
    },

    async refreshSlabsCache() {
      try {
        await this.$store.dispatch('masterData/refreshSlabs');
      } catch (error) {
        console.error('Error refreshing slabs:', error);
      }
    },

    async refreshProcessesCache() {
      try {
        await this.$store.dispatch('masterData/refreshProcesses');
      } catch (error) {
        console.error('Error refreshing processes:', error);
      }
    },

    async refreshIndustriesCache() {
      try {
        await this.$store.dispatch('masterData/refreshIndustries');
      } catch (error) {
        console.error('Error refreshing industries:', error);
      }
    },

    async refreshCustomerGradesCache() {
      try {
        await this.$store.dispatch('masterData/refreshCustomerGrades');
      } catch (error) {
        console.error('Error refreshing customer grades:', error);
      }
    },

    async refreshFavouriteDetailsCache() {
      try {
        await this.$store.dispatch('masterData/refreshFavouriteDetails');
      } catch (error) {
        console.error('Error refreshing favourite details:', error);
      }
    },

    async refreshFinancialsInputDetailsCache() {
      try {
        await this.$store.dispatch('masterData/refreshFinancialsInputDetails');
      } catch (error) {
        console.error('Error refreshing financials input details:', error);
      }
    },

    /**
     * Fetch industries data on-demand
     * Call this in checklist pages that need industry data
     */
    async fetchIndustriesDataIfNeeded() {
      try {
        console.log('[fetchIndustriesDataIfNeeded] Starting fetch...');
        await this.$store.dispatch('masterData/fetchIndustriesData');
        console.log('[fetchIndustriesDataIfNeeded] Fetch completed, INDUSTRIES_DATASET:', this.INDUSTRIES_DATASET);
      } catch (error) {
        console.error('Error fetching industries data:', error);
      }
    },

    /**
     * Fetch checklist page data in a single API call
     * Combines: forms_master, form_maintenance, process, industry datasets
     * Call this in act_checklist pages to load all required data at once
     */
    async fetchChecklistPageData() {
      try {
        console.log('[fetchChecklistPageData] Starting combined fetch...');
        await this.$store.dispatch('masterData/fetchChecklistPageData');
        console.log('[fetchChecklistPageData] Fetch completed');
      } catch (error) {
        console.error('Error fetching checklist page data:', error);
      }
    }
  }
};
